// Data client for XBG Backlog.
//
// The app used to talk to Supabase. It now talks to our own stack: PostgREST
// over Cloud SQL for queries, plus a small Express service for the things
// PostgREST doesn't do (auth, file storage, change streaming, AI calls).
//
// The query surface is deliberately identical, because Supabase's `.from()`
// builder IS postgrest-js — so every `.from(...).select(...)` and `.rpc(...)`
// call in the app works unchanged against our own PostgREST. Only auth,
// storage, realtime and functions needed shims, and they are below.

import { PostgrestClient } from "@supabase/postgrest-js";

import type { Database } from "./types.generated";

type DbSchema = Database["public"];
type TableName = keyof DbSchema["Tables"] & string;
type DbFunctions = DbSchema["Functions"];
type FunctionName = keyof DbFunctions & string;

// postgrest-js builds a URL object internally, so this must be absolute.
const REST_URL = typeof window !== "undefined" ? `${window.location.origin}/rest/v1` : "http://127.0.0.1:8080/rest/v1";
const API_URL = "/api";
const SESSION_KEY = "xbg.session";

// The session lives in a cookie, not localStorage, so it can be scoped to the
// parent domain every tenant subdomain shares (COOKIE_DOMAIN → GET /api/tenant →
// setCookieDomain). That is what lets a tenant switch redirect to another
// tenant's subdomain and land already signed in. It stays JS-readable (not
// HttpOnly) because the token is read here to set the Authorization header and
// the SSE query — XSS exposure is unchanged from the localStorage it replaces.
// Empty domain (dev / single host) → a host-only cookie, and switching happens
// in place.
let cookieDomain: string | null = null;

/** Called once GET /api/tenant resolves, so writes scope the cookie correctly. */
export function setCookieDomain(domain: string | null): void {
  cookieDomain = domain && domain.trim() ? domain.trim() : null;
}

function readSessionCookie(): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${SESSION_KEY}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function writeSessionCookie(value: string | null, maxAgeSeconds = 0): void {
  if (typeof document === "undefined") return;
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  const domain = cookieDomain ? `; Domain=${cookieDomain}` : "";
  if (value === null) {
    // Clear both the parent-scoped and any host-only variant — the cookie may
    // have been written either way depending on when cookieDomain was known.
    document.cookie = `${SESSION_KEY}=; Path=/; Max-Age=0; SameSite=Lax${domain}${secure}`;
    document.cookie = `${SESSION_KEY}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    return;
  }
  document.cookie = `${SESSION_KEY}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${domain}${secure}`;
}

// ------------------------------------------------------------------ types --
export interface AuthUser {
  id: string;
  email: string;
}

export interface Session {
  access_token: string;
  expires_at: number; // epoch seconds
  user: AuthUser;
}

/** A tenant the signed-in user may act in, from GET /api/auth/tenants. */
export interface SwitchableTenant {
  id: string;
  slug: string;
  name: string;
  /** Web path to the tenant's logo asset, or null. */
  logo: string | null;
  /** The tenant's original/bundled logo, before any upload — never changed
   *  by an upload, so comparing it against `logo` is how the Company page
   *  knows whether there's an upload to revert. */
  default_logo: string | null;
  flags: string[];
  primary_host: string | null;
  /** Support/contact address shown on the login page's "Request an invite"
   *  mailto, or null if unset (the link hides itself then). */
  contact_email: string | null;
  /** What the AI rewrite button says here, or null for the default (#9401). */
  ai_rewrite_label: string | null;
  /** Whether this is the session's current tenant. */
  current: boolean;
  /** Whether this is the one the user starred — where signing in at the shared
   *  host sends them. At most one is true, and a star on a workspace they have
   *  since left simply lights none of them. */
  preferred: boolean;
}

interface Result<T> {
  data: T;
  error: { message: string } | null;
}

const ok = <T>(data: T): Result<T> => ({ data, error: null });
const fail = <T>(data: T, message: string): Result<T> => ({ data, error: { message } });

// ---------------------------------------------------------------- session --
let session: Session | null = null;

try {
  const raw = readSessionCookie();
  if (raw) {
    const parsed = JSON.parse(raw) as Session;
    // Drop an already-expired session rather than sending a dead token.
    if (parsed?.expires_at && parsed.expires_at * 1000 > Date.now()) session = parsed;
    else writeSessionCookie(null);
  }
} catch {
  /* corrupt or unavailable storage — start signed out */
}

// SESSION_EXPIRED is a SIGNED_OUT that specifically means "a live session just
// died on us" (12h expiry or a 401 revoking the token) — as opposed to a plain
// SIGNED_OUT, which covers a deliberate sign-out and the never-signed-in state.
// AppLayout uses the distinction to keep an active user on their page behind a
// re-login prompt, while sending a fresh/logged-out visitor to /login. Every
// other listener only branches on whether a session is present, so the extra
// event name is transparent to them.
type AuthEvent = "SIGNED_IN" | "SIGNED_OUT" | "SESSION_EXPIRED" | "TOKEN_REFRESHED" | "USER_UPDATED";
type AuthListener = (event: AuthEvent, session: Session | null) => void;
const listeners = new Set<AuthListener>();

function setSession(next: Session | null, event: AuthEvent) {
  session = next;
  try {
    if (next) {
      const maxAge = Math.max(0, next.expires_at - Math.floor(Date.now() / 1000));
      writeSessionCookie(JSON.stringify(next), maxAge);
    } else {
      writeSessionCookie(null);
    }
  } catch {
    /* storage unavailable — session stays in memory for this tab */
  }
  // The SSE stream authenticates with the session token in its URL, so a token
  // change (login, tenant switch) or sign-out leaves the open stream stale.
  // Drop it and, if still signed in with live channels, reopen with the new one.
  reconnectStream();
  listeners.forEach((cb) => cb(event, next));
}

export const currentAccessToken = (): string | null => session?.access_token ?? null;

// Sessions die in place: there is no refresh endpoint, so a token that hits
// its 12h expiry (or is revoked server-side, surfacing as a 401) can only be
// replaced by signing in again. Detect both here — every data path funnels
// through liveSession()/apiFetch/rest — and emit SESSION_EXPIRED so AppLayout
// prompts an active user to re-login (keeping their page) instead of rendering
// empty boards with a dead token.
const isExpired = (s: Session | null): boolean => !!s && s.expires_at * 1000 <= Date.now();

function dropDeadSession() {
  if (session) setSession(null, "SESSION_EXPIRED");
}

/** The session, re-checked against its expiry rather than trusted from load time. */
function liveSession(): Session | null {
  if (isExpired(session)) dropDeadSession();
  return session;
}

// ---------------------------------------------------------------- helpers --
async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<Result<T | null>> {
  const s = liveSession();
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
  if (s) headers.set("Authorization", `Bearer ${s.access_token}`);

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...init, headers });
  } catch {
    return fail(null, "Network error");
  }

  // A 401 while holding a session means the token is dead (expired or
  // revoked) — no retry can succeed, so sign out rather than fail quietly.
  if (res.status === 401) dropDeadSession();

  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    return fail(null, (body as { error?: string })?.error ?? `Request failed (${res.status})`);
  }
  return ok(body as T);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------- auth --
class AuthShim {
  async getSession(): Promise<Result<{ session: Session | null }>> {
    return ok({ session: liveSession() });
  }

  async getUser(): Promise<Result<{ user: AuthUser | null }>> {
    return ok({ user: liveSession()?.user ?? null });
  }

  onAuthStateChange(cb: AuthListener) {
    listeners.add(cb);
    // Supabase fires an initial event on subscribe; mirror that so callers
    // that only react to the callback still settle on first paint.
    queueMicrotask(() => cb(session ? "SIGNED_IN" : "SIGNED_OUT", session));
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            listeners.delete(cb);
          },
        },
      },
    };
  }

  /**
   * Sign in. At a tenant's own host that is the whole story. At the shared
   * "Welcome" host the server also answers WHERE this session belongs:
   * `redirect_host` is the workspace to land on when the choice was
   * unambiguous (one membership, or a starred one), and null when the user
   * still has a choice to make — the caller then leaves them on the landing
   * page, which lists their workspaces. The session itself is already minted
   * for a real membership either way, so a caller that ignores the field is
   * signed in, just possibly on the wrong host to notice it.
   */
  async signInWithPassword(creds: { email: string; password: string }) {
    const res = await apiFetch<Session & { redirect_host?: string | null }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(creds),
    });
    if (res.error || !res.data)
      return fail({ user: null, session: null, redirectHost: null }, res.error?.message ?? "Sign-in failed");
    // Split the routing hint off the session before storing it: the cookie
    // carries credentials, not a one-shot answer about where to navigate next.
    const { redirect_host: redirectHost = null, ...session } = res.data;
    setSession(session, "SIGNED_IN");
    return ok({ user: session.user, session, redirectHost });
  }

  async signUp(args: {
    email: string;
    password: string;
    // The invitation token from the emailed link. Since #3373 the server
    // authorises signup on this rather than on a known address, and reads the
    // email FROM the invitation — so a valid token cannot be pointed at someone
    // else's address.
    token?: string;
    options?: { data?: Record<string, unknown>; emailRedirectTo?: string };
  }) {
    const res = await apiFetch<Session>("/auth/signup", {
      method: "POST",
      body: JSON.stringify({
        email: args.email,
        password: args.password,
        token: args.token,
        metadata: args.options?.data ?? {},
      }),
    });
    if (res.error || !res.data) return fail({ user: null, session: null }, res.error?.message ?? "Sign-up failed");
    setSession(res.data, "SIGNED_IN");
    return ok({ user: res.data.user, session: res.data });
  }

  async signOut() {
    setSession(null, "SIGNED_OUT");
    return ok(null);
  }

  /** The tenants the signed-in user may switch into, and whether they may
   *  manage tenant records (GET /api/auth/tenants). */
  async listTenants(): Promise<Result<{ tenants: SwitchableTenant[]; isPlatformAdmin: boolean }>> {
    const res = await apiFetch<{ tenants: SwitchableTenant[]; is_platform_admin?: boolean }>("/auth/tenants");
    if (res.error || !res.data) {
      return fail({ tenants: [], isPlatformAdmin: false }, res.error?.message ?? "Could not load workspaces");
    }
    return ok({ tenants: res.data.tenants ?? [], isPlatformAdmin: res.data.is_platform_admin === true });
  }

  /**
   * Re-mint the session for another tenant the user belongs to. The server
   * re-checks membership (there is no cross-tenant super-role), so a caller can
   * never mint a token for a tenant they do not hold a role in. On success the
   * new token is stored — into the parent-domain cookie when one is configured,
   * so a subsequent redirect to that tenant's subdomain lands signed in.
   */
  async switchTenant(tenantId: string): Promise<Result<{ session: Session | null }>> {
    const res = await apiFetch<Session>("/auth/switch-tenant", {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    });
    if (res.error || !res.data) return fail({ session: null }, res.error?.message ?? "Could not switch workspace");
    setSession(res.data, "SIGNED_IN");
    return ok({ session: res.data });
  }

  /**
   * Star a workspace, or pass null to clear the star. It decides nothing about
   * the current session — only where signing in at the shared host will send
   * this person next time. The server re-checks membership, so this cannot
   * point someone at a workspace they do not belong to.
   */
  async setPreferredTenant(tenantId: string | null): Promise<Result<{ preferredTenantId: string | null }>> {
    const res = await apiFetch<{ preferred_tenant_id: string | null }>("/auth/preferred-tenant", {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    });
    if (res.error || !res.data)
      return fail({ preferredTenantId: null }, res.error?.message ?? "Could not set your default workspace");
    return ok({ preferredTenantId: res.data.preferred_tenant_id ?? null });
  }

  /**
   * Update the caller's own tenant (superadmin only) — rename it, or point
   * its logo at whatever was just uploaded to the tenant-logos storage
   * bucket. PATCH /api/tenant derives the logo URL itself when `logoUploaded`
   * is set, so the client never constructs (or is trusted to hand back) a
   * storage URL by hand.
   */
  async updateTenant(patch: {
    name?: string;
    logo?: string | null;
    logoUploaded?: true;
    revertLogo?: true;
    contact_email?: string | null;
    notifications_enabled?: boolean;
    ai_rewrite_label?: string | null;
  }) {
    const res = await apiFetch<{
      id: string;
      name: string;
      logo: string | null;
      flags: string[];
      is_default: boolean;
      contact_email: string | null;
      ai_rewrite_label: string | null;
    }>("/tenant", { method: "PATCH", body: JSON.stringify(patch) });
    if (res.error || !res.data) return fail(null, res.error?.message ?? "Could not update the workspace");
    return ok(res.data);
  }

  /**
   * Updates an account. Omit `userId` to edit your own; an admin may pass
   * another user's id, which the server authorises from the JWT.
   */
  async updateUser(attrs: { password?: string; email?: string; userId?: string; data?: Record<string, unknown> }) {
    const res = await apiFetch<{ user: AuthUser }>("/auth/user", {
      method: "PATCH",
      body: JSON.stringify(attrs),
    });
    if (res.error || !res.data) return fail({ user: null }, res.error?.message ?? "Update failed");
    // Only adopt the response into the local session when it describes the
    // signed-in user — an admin editing someone else must not be swapped into
    // that person's identity.
    const editedSelf = !attrs.userId || attrs.userId === session?.user.id;
    if (session && editedSelf) setSession({ ...session, user: res.data.user }, "USER_UPDATED");
    return ok({ user: res.data.user });
  }

  /**
   * Finish a reset using the single-use token from the email link. On success
   * the server returns a fresh session, so the user lands signed in.
   */
  async completePasswordReset(token: string, password: string) {
    const res = await apiFetch<Session>("/auth/reset-confirm", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
    if (res.error || !res.data) {
      return fail({ session: null }, res.error?.message ?? "Could not reset the password");
    }
    setSession(res.data, "SIGNED_IN");
    return ok({ session: res.data });
  }

  async resetPasswordForEmail(email: string, _opts?: { redirectTo?: string }) {
    // Unknown addresses still get a 200 from the server (account-enumeration
    // guard); an error here means the email genuinely could not be sent.
    const res = await apiFetch("/auth/reset-request", { method: "POST", body: JSON.stringify({ email }) });
    if (res.error) return fail({}, res.error.message);
    return ok({});
  }
}

// ---------------------------------------------------------------- storage --
// Backed by GCS. Uploads go through the API so the browser never holds
// bucket credentials; reads use a signed-URL redirect endpoint.
class StorageBucket {
  constructor(private bucket: string) {}

  async upload(path: string, file: File, opts?: { upsert?: boolean }) {
    // Raw body rather than multipart — one less parser on the server.
    const qs = new URLSearchParams({
      bucket: this.bucket,
      path,
      ...(opts?.upsert ? { upsert: "true" } : {}),
    });

    const headers = new Headers({ "Content-Type": file.type || "application/octet-stream" });
    if (session) headers.set("Authorization", `Bearer ${session.access_token}`);

    try {
      const res = await fetch(`${API_URL}/storage/upload?${qs}`, { method: "POST", body: file, headers });
      if (!res.ok) {
        const body = safeJson(await res.text()) as { error?: string } | null;
        return fail({ path: null }, body?.error ?? `Upload failed (${res.status})`);
      }
      return ok({ path });
    } catch {
      return fail({ path: null }, "Network error during upload");
    }
  }

  async remove(paths: string[]) {
    const res = await apiFetch("/storage/remove", {
      method: "POST",
      body: JSON.stringify({ bucket: this.bucket, paths }),
    });
    return res.error ? fail(null, res.error.message) : ok(null);
  }

  getPublicUrl(path: string) {
    // Not literally public — the endpoint authorizes then redirects to a
    // short-lived signed GCS URL. Shape matches Supabase's for drop-in use.
    return {
      data: {
        publicUrl: `${API_URL}/storage/object/${encodeURIComponent(this.bucket)}/${encodeURIComponent(path)}`,
      },
    };
  }
}

class StorageShim {
  from(bucket: string) {
    return new StorageBucket(bucket);
  }
}

// --------------------------------------------------------------- realtime --
// Postgres LISTEN/NOTIFY -> the API's SSE endpoint -> these channels.
//
// The trigger deliberately sends NO row data — the payload is {table, op, id}
// plus, for backlog_items UPDATEs, two booleans describing WHICH columns
// changed (see notify_backlog_change() in docs/schema.sql). Consumers react
// by invalidating or refetching under their own RLS-scoped read; a payload
// filter is impossible by design, which is why on() takes none.

/**
 * A change notification. `id` is the changed row's primary key; the two
 * optional flags arrive only on backlog_items UPDATEs.
 */
export interface ChangePayload {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  table: string;
  id: string;
  /** UPDATE touched nothing but row_number — reorder churn most views skip. */
  row_number_only?: boolean;
  /** UPDATE flipped deleted_at — a soft delete or restore. */
  deleted_at_changed?: boolean;
}

interface Binding {
  event: string;
  table: string;
  cb: (payload: ChangePayload) => void;
}

let eventSource: EventSource | null = null;
const channels = new Set<RealtimeChannel>();

function ensureStream() {
  if (eventSource || !session) return;
  eventSource = new EventSource(`${API_URL}/events?token=${encodeURIComponent(session.access_token)}`);
  eventSource.onmessage = (msg) => {
    let evt: { table?: string; op?: string; id?: string; row_number_only?: boolean; deleted_at_changed?: boolean };
    try {
      evt = JSON.parse(msg.data);
    } catch {
      return;
    }
    if (!evt.table || !evt.op || !evt.id) return;
    channels.forEach((ch) =>
      ch.dispatch({
        table: evt.table!,
        op: evt.op!,
        id: evt.id!,
        row_number_only: evt.row_number_only,
        deleted_at_changed: evt.deleted_at_changed,
      }),
    );
  };
  eventSource.onerror = () => {
    // EventSource reconnects on its own; drop the handle if it closed for good.
    if (eventSource?.readyState === EventSource.CLOSED) eventSource = null;
  };
}

/** Close the stream and reopen it with the current token, if any channels are
 *  live. Called from setSession so a tenant switch re-scopes realtime. */
function reconnectStream() {
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
  if (session && channels.size > 0) ensureStream();
}

class RealtimeChannel {
  private bindings: Binding[] = [];
  constructor(public name: string) {}

  on(
    _type: "postgres_changes",
    opts: { event: string; schema: string; table: string },
    cb: (payload: ChangePayload) => void,
  ) {
    this.bindings.push({ event: opts.event, table: opts.table, cb });
    return this;
  }

  subscribe() {
    channels.add(this);
    ensureStream();
    return this;
  }

  dispatch(evt: { table: string; op: string; id: string; row_number_only?: boolean; deleted_at_changed?: boolean }) {
    for (const b of this.bindings) {
      if (b.table !== evt.table) continue;
      if (b.event !== "*" && b.event !== evt.op) continue;
      b.cb({
        eventType: evt.op as ChangePayload["eventType"],
        table: evt.table,
        id: evt.id,
        row_number_only: evt.row_number_only,
        deleted_at_changed: evt.deleted_at_changed,
      });
    }
  }

  teardown() {
    channels.delete(this);
    if (channels.size === 0 && eventSource) {
      eventSource.close();
      eventSource = null;
    }
  }
}

// -------------------------------------------------------------- functions --
class FunctionsShim {
  async invoke<T = unknown>(name: string, args?: { body?: unknown }) {
    return apiFetch<T>(`/${name}`, {
      method: "POST",
      body: JSON.stringify(args?.body ?? {}),
    });
  }
}

// ----------------------------------------------------------------- client --
class XbgClient {
  auth = new AuthShim();
  storage = new StorageShim();
  functions = new FunctionsShim();

  // Rebuilt per call so the Authorization header always carries the live token
  // (postgrest-js snapshots headers at construction).
  private get rest() {
    const s = liveSession();
    const headers: Record<string, string> = {};
    if (s) headers.Authorization = `Bearer ${s.access_token}`;
    return new PostgrestClient<Database>(REST_URL, {
      headers,
      // PostgREST traffic bypasses apiFetch, so the dead-token check rides the
      // fetch it is given: a 401 with a session held means expired/revoked.
      fetch: async (input, init) => {
        const res = await fetch(input, init);
        if (res.status === 401) dropDeadSession();
        return res;
      },
    });
  }

  /**
   * Query a table. Typed against the generated schema, so selecting a column
   * that does not exist is a typecheck error rather than a 400 at runtime.
   */
  from<T extends TableName>(table: T) {
    return this.rest.from(table);
  }

  /** Call a Postgres function. Argument names and types come from the schema. */
  rpc<F extends FunctionName>(fn: F, args?: DbFunctions[F]["Args"]) {
    return this.rest.rpc(fn, (args ?? {}) as DbFunctions[F]["Args"]);
  }

  channel(name: string) {
    return new RealtimeChannel(name);
  }

  removeChannel(channel: RealtimeChannel) {
    channel.teardown();
  }
}

export const supabase = new XbgClient();
export const db = supabase;
